import React, {useMemo, useState} from 'react';
import {Notification} from '../navigation/page-layout';
import {ServiceResource, useAuth} from '../hooks/use-auth';
import {F3ExcelsiorForecastStoreLambda} from '@amzn/f3-excelsior-forecast-store-lambda';
import {translateErrorToReactNode} from '../common';
import {
    useDownloadOriginalUploadArtifact,
    useGetForecastAssetUploadStatus,
    useGetAllForecastHistoryOfTimeRangeOrLatest,
    ForecastAuditHistoryWithCreatedTime,
} from '../hooks/use-forecast-store-api';
import {Box, Button, Header, Modal, StatusIndicator, Table} from '@amzn/awsui-components-react';
import Timestamp from '../common/timestamp';
import '../../assets/styles/orchestrator-styles.scss';
import PreviewTemplateButton from './preview-template-button';

const DAYS_TO_INSPECT = 28; // 4 weeks
const MAX_FORECASTS_TO_DISPLAY = 5;

type ForecastAudit = F3ExcelsiorForecastStoreLambda.Types.ForecastAudit;

interface AuditRow {
    type: 'audit';
    key: string;
    forecastId: string;
    audit: ForecastAudit;
}

interface ForecastGroupRow {
    type: 'forecast';
    key: string;
    forecast: ForecastAuditHistoryWithCreatedTime;
    isLatest: boolean;
    children: AuditRow[];
}

type HistoryRow = ForecastGroupRow | AuditRow;

const formatForecastDate = (date: Date) =>
    date.toLocaleString('en-us', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZoneName: 'short',
    });

const getForecastLabel = (row: ForecastGroupRow) =>
    `${row.isLatest ? 'Latest Forecast' : 'Forecast'} generated ${formatForecastDate(row.forecast.createdTime)}`;

const getEventCountLabel = (count: number) => (count === 0 ? 'No adjustments' : `${count} ${count === 1 ? 'event' : 'events'}`);

// Newest forecasts first, capped, each as a group row whose children are its audit rows.
const buildHistoryRows = (history: ForecastAuditHistoryWithCreatedTime[]): ForecastGroupRow[] =>
    [...history]
        .sort((a, b) => b.createdTime.getTime() - a.createdTime.getTime())
        .slice(0, MAX_FORECASTS_TO_DISPLAY)
        .map((forecast, index) => ({
            type: 'forecast',
            key: `forecast:${forecast.forecastId}`,
            forecast,
            isLatest: index === 0,
            children: (forecast.audits || []).map((audit, auditIndex) => ({
                type: 'audit',
                key: `audit:${forecast.forecastId}:${auditIndex}`,
                forecastId: forecast.forecastId,
                audit,
            })),
        }));

const extractUploadErrorMessage = (response?: F3ExcelsiorForecastStoreLambda.Types.GetForecastAssetUploadStatusResponse) => {
    if (response?.fileValidationErrors) {
        return response.fileValidationErrors.map((error, index) => (
            <div data-testid={index} key={index}>
                {index + 1}
                {')'} {error}
            </div>
        ));
    } else if (response?.serviceError) {
        return <React.Fragment>{response.serviceError}</React.Fragment>;
    } else if (response?.bulkOverrideValidationErrors?.['bulkOverrideValueValidations']?.errors?.length) {
        return response.bulkOverrideValidationErrors['bulkOverrideValueValidations'].errors.map((error, index) => (
            <div data-testid={index} key={index}>
                {index + 1}
                {')'} {error}
            </div>
        ));
    } else if (response?.skippedGranularityWarnings && response.skippedGranularityWarnings.length > 0) {
        return (
            <>
                <div style={{marginBottom: '8px', fontWeight: 'bold'}}>
                    {response.skippedGranularityWarnings.length} row(s) were skipped due to invalid granularity:
                    {response.isExcessiveSkippedGranularityWarnings && ' (list truncated)'}
                </div>
                {response.skippedGranularityWarnings.map((warning, index) => (
                    <div data-testid={index} key={index}>
                        {index + 1}) {warning}
                    </div>
                ))}
            </>
        );
    }
    return <div>No issues found.</div>;
};

export default function ViewForecastHistory(props: {pushNotification: (notification: Notification) => void}) {
    const auth = useAuth();
    const [visible, setVisible] = useState(false);

    const forecastStoreViewClientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ForecastStoreView);

    function createErrorListener<T>(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const {
        status: getAllForecastHistoryOfTimeRangeStatus,
        value: getAllForecastHistoryOfTimeRangeResponse,
        execute: getAllForecastHistoryOfTimeRange,
    } = useGetAllForecastHistoryOfTimeRangeOrLatest(
        forecastStoreViewClientConfiguration,
        {
            businessId: auth.authInformation!.current!.businessId,
            country: auth.authInformation!.current!.country,
            flow: auth.authInformation!.current!.flow,
            daysToInspect: DAYS_TO_INSPECT,
        },
        true,
        createErrorListener('GetAllForecastHistoryOfTimeRangeOrLatest failed'),
        [auth]
    );
    const groupRows = useMemo(
        () => buildHistoryRows(getAllForecastHistoryOfTimeRangeResponse ?? []),
        [getAllForecastHistoryOfTimeRangeResponse]
    );
    // null means "use the default": only the latest forecast expanded (if it has any audits).
    const [expandedKeys, setExpandedKeys] = useState<string[] | null>(null);
    const effectiveExpandedKeys =
        expandedKeys ?? (groupRows.length > 0 && groupRows[0].children.length > 0 ? [groupRows[0].key] : []);
    const expandedItems = groupRows.filter((row) => effectiveExpandedKeys.includes(row.key));

    const {value: getForecastAssetUploadStatusResponse, execute: executeGetForecastAssetUploadStatus} =
        useGetForecastAssetUploadStatus(
            forecastStoreViewClientConfiguration,
            createErrorListener('GetForecastAssetStatus failed'),
            [auth]
        );

    const {execute: executeDownloadOriginalUploadArtifact} = useDownloadOriginalUploadArtifact(
        forecastStoreViewClientConfiguration,
        createErrorListener('GetOriginalUploadDownloadUrl failed'),
        [auth]
    );

    const isLoading = getAllForecastHistoryOfTimeRangeStatus === 'idle' || getAllForecastHistoryOfTimeRangeStatus === 'pending';

    const columnDefinitions = [
        {
            id: 'startTime',
            header: 'Start Time',
            cell: (row: HistoryRow) =>
                row.type === 'forecast' ? (
                    <Box fontWeight="bold">{getForecastLabel(row)}</Box>
                ) : (
                    <Timestamp datetime={row.audit.startTime} />
                ),
            minWidth: '150px',
        },
        {
            id: 'eventName',
            header: 'Event Name',
            cell: (row: HistoryRow) => (row.type === 'forecast' ? getEventCountLabel(row.children.length) : row.audit.eventName),
            minWidth: '150px',
        },
        {
            id: 'templateName',
            header: 'Template Name',
            cell: (row: HistoryRow) => (row.type === 'forecast' ? null : row.audit.attachment?.templateName),
            minWidth: '150px',
        },
        {
            id: 'endTime',
            header: 'End Time',
            cell: (row: HistoryRow) => {
                if (row.type === 'forecast') {
                    return null;
                }
                const item = row.audit;
                if (item.endTime) {
                    return <Timestamp datetime={item.endTime} />;
                } else {
                    return '';
                }
            },
            minWidth: '150px',
        },
        {
            id: 'initiator',
            header: 'Initiator',
            cell: (row: HistoryRow) => (row.type === 'forecast' ? null : row.audit.initiator),
            minWidth: '100px',
        },
        {
            id: 'status',
            header: 'Status',
            cell: (row: HistoryRow) => {
                if (row.type === 'forecast') {
                    return null;
                }
                const item = row.audit;
                const indicatorType =
                    item.status === 'succeeded'
                        ? 'success'
                        : item.status === 'queued'
                        ? 'pending'
                        : item.status === 'in_progress'
                        ? 'in-progress'
                        : 'error';

                return (
                    <StatusIndicator data-testid="override-status" type={indicatorType}>
                        {item.status}
                    </StatusIndicator>
                );
            },
            minWidth: '100px',
        },
        {
            id: 'action',
            header: 'Action',
            cell: (row: HistoryRow) => {
                if (row.type === 'forecast') {
                    return null;
                }
                const item = row.audit;
                if (
                    [
                        'BULK_OVERRIDE',
                        'MARKETING_EVENT',
                        'RESET_SELECTED_OVERRIDES',
                        'BASELINE_WEEK_OVERRIDE',
                        'DOW_RATIO',
                    ].includes(item.eventName)
                ) {
                    return (
                        <>
                            <Button
                                data-testid="download-upload-artifact"
                                iconName="download"
                                variant="icon"
                                onClick={async () => {
                                    await executeDownloadOriginalUploadArtifact({
                                        forecastId: row.forecastId,
                                        uploadId: item.attachment?.uploadId || '',
                                    });
                                }}
                            />
                            <PreviewTemplateButton
                                forecastId={row.forecastId}
                                uploadId={item.attachment?.uploadId || ''}
                                pushNotification={props.pushNotification}
                            />
                        </>
                    );
                }
                return;
            },
            minWidth: '100px',
        },
        {
            id: 'info',
            header: 'Info',
            cell: (row: HistoryRow) => {
                if (row.type === 'forecast') {
                    return null;
                }
                const item = row.audit;
                const isFailedUpload =
                    [
                        'BULK_OVERRIDE',
                        'MARKETING_EVENT',
                        'RESET_SELECTED_OVERRIDES',
                        'BASELINE_WEEK_OVERRIDE',
                        'DOW_RATIO',
                    ].includes(item.eventName) && item.status === 'failed';
                // Only bulk override uploads can produce skipped-granularity warnings, so we
                // surface the Info affordance for succeeded uploads on BULK_OVERRIDE only.
                const isSucceededBulkOverride = item.eventName === 'BULK_OVERRIDE' && item.status === 'succeeded';
                if (isFailedUpload || isSucceededBulkOverride) {
                    return (
                        <Button
                            data-testid="open-upload-error-modal"
                            iconName="status-info"
                            variant="icon"
                            onClick={async () => {
                                await executeGetForecastAssetUploadStatus({
                                    uploadId: item.attachment?.uploadId || '',
                                });
                                setVisible(true);
                            }}
                        />
                    );
                }
                return;
            },
            minWidth: '100px',
        },
    ];

    return (
        <React.Fragment>
            <div className="main-header">
                <Header variant="h2">Forecast History</Header>
            </div>
            <div className="main-content">
                <Table
                    data-testid="history"
                    columnDefinitions={columnDefinitions}
                    items={groupRows}
                    trackBy={(row: HistoryRow) => row.key}
                    expandableRows={{
                        getItemChildren: (row: HistoryRow) => (row.type === 'forecast' ? row.children : []),
                        isItemExpandable: (row: HistoryRow) => row.type === 'forecast' && row.children.length > 0,
                        expandedItems,
                        onExpandableItemToggle: ({detail}) =>
                            setExpandedKeys(
                                detail.expanded
                                    ? [...effectiveExpandedKeys, detail.item.key]
                                    : effectiveExpandedKeys.filter((key) => key !== detail.item.key)
                            ),
                    }}
                    ariaLabels={{
                        expandButtonLabel: (row: HistoryRow) =>
                            row.type === 'forecast' ? `Expand ${getForecastLabel(row)}` : 'Expand',
                        collapseButtonLabel: (row: HistoryRow) =>
                            row.type === 'forecast' ? `Collapse ${getForecastLabel(row)}` : 'Collapse',
                    }}
                    loading={isLoading}
                    loadingText="Loading history"
                    header={
                        <Header
                            actions={
                                <Button
                                    data-testid="refresh"
                                    iconName="refresh"
                                    variant="normal"
                                    onClick={async () => {
                                        setExpandedKeys(null);
                                        await getAllForecastHistoryOfTimeRange();
                                    }}
                                />
                            }
                        >
                            Forecasts
                        </Header>
                    }
                    empty={
                        <Box textAlign="center" color="inherit">
                            <b>No forecast history</b>
                        </Box>
                    }
                />
                <Modal
                    data-testid="upload-error-modal"
                    onDismiss={() => setVisible(false)}
                    visible={visible}
                    closeAriaLabel="Close modal"
                    size="large"
                    header="Upload Details"
                >
                    {extractUploadErrorMessage(getForecastAssetUploadStatusResponse)}
                </Modal>
            </div>
        </React.Fragment>
    );
}
